import { mdiDelete, mdiEmail, mdiEye, mdiPlus } from "@mdi/js";
import { Button, ModalFuncProps, Space, Tag, notification } from "antd";
import confirm from "antd/lib/modal/confirm";
import { UserListItemVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import BooleanDisplay from "components/ui/BooleanDisplay";
import IconButton from "components/ui/IconButton";
import useTable from "hooks/useTable";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "widgets/table/CustomTable";
import { ColumnStorageName } from "widgets/table/table-settings/ColumnStorageName";
import {
  ColumnState,
  ExtendedColumnType,
} from "widgets/table/table-settings/ExtendedColumnType";
import {
  ColumnConfigParams,
  FilterMode,
  ListRequestParams,
} from "widgets/table/useTableUtils";
import { Permission } from "components/Permission";
import Claims from "features/auth/claims";
import UserCreateModal from "./UserCreateModal";

const UsersList: FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { usersListParams: listParams, users } = useSelector(
    (state) => state.usersListSettings
  );
  const { fetchUsers } = useSelector(
    (state) => state.loading.effects.usersListSettings
  );

  useEffect(() => {
    dispatch.usersListSettings.resetUsersListParams();
  }, [dispatch.usersListSettings]);

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchTableData = useCallback(
    (requestParams: ListRequestParams) =>
      dispatch.usersListSettings.fetchUsers(requestParams),
    [dispatch.usersListSettings]
  );

  const columnParams = useMemo<ColumnConfigParams<UserListItemVm>[]>(
    () => [
      {
        key: "familyname",
        title: t("settings.users.familyName"),
        defaultState: ColumnState.AlwaysVisible,
        sort: true,
        filterMode: FilterMode.SEARCH,
      },
      {
        key: "middlename",
        sort: true,
        defaultState: ColumnState.AlwaysVisible,
        filterMode: FilterMode.SEARCH,
        title: t("settings.users.middleName"),
      },
      {
        key: "forename",
        title: t("settings.users.forename"),
        defaultState: ColumnState.AlwaysVisible,
        sort: true,
        filterMode: FilterMode.SEARCH,
      },
      {
        key: "username",
        title: t("settings.users.username"),
        defaultState: ColumnState.AlwaysVisible,
        sort: true,
        filterMode: FilterMode.SEARCH,
      },
      {
        key: "orgName",
        title: t("settings.users.orgName"),
        defaultState: ColumnState.AlwaysVisible,
        sort: true,
        filterMode: FilterMode.SEARCH,
      },
      {
        key: "isRegistered",
        title: t("settings.users.isRegistered"),
        defaultState: ColumnState.AlwaysVisible,
        sort: true,
        filterMode: FilterMode.YES_NO,
        render: (value: boolean) => {
          return (
            <BooleanDisplay
              value={value}
              trueDisplay={<Tag color="success">{t("common.yes")}</Tag>}
              falseDisplay={<Tag color="error">{t("common.no")}</Tag>}
            />
          );
        },
      },
    ],
    [t]
  );

  const handleViewButtonClick = useCallback(
    (id?: string) => {
      dispatch.usersListSettings.setEditingUserId(id);
      setCreateModalOpen(true);
    },
    [dispatch.usersListSettings]
  );

  const handleInviteButtonClick = useCallback(
    async (id?: string) => {
      try {
        if (id) {
          await dispatch.usersListSettings.inviteUser(id);
          notification.success({
            message: t("settings.users.inviteUserSuccess"),
          });
        }
      } catch (error) {
        // Note: error handled globally in errorHandlingMiddleware
      }
    },
    [dispatch.usersListSettings, t]
  );

  const handleDeleteButtonClick = useCallback(
    (id?: string) => {
      if (!id) return;

      const confirmProps: ModalFuncProps = {
        okText: t("common.confirmDeleteModal.yes"),
        okType: "danger",
        cancelText: t("common.confirmDeleteModal.no"),
        title: t("common.confirmDeleteModal.title"),
        onOk: async () => {
          await dispatch.usersListSettings.deleteUser(id);

          return dispatch.usersListSettings.fetchUsers(listParams);
        },
      };

      confirm(confirmProps);
    },
    [dispatch.usersListSettings, t, listParams]
  );

  const actionColumnParams = useMemo<
    Partial<ExtendedColumnType<UserListItemVm>> | undefined
  >(
    () => ({
      fixed: "right",
      columnOrderFixed: true,
      width: 130,
      render(item: any, record) {
        return (
          <Space>
            <IconButton
              path={mdiEye}
              onClick={() => handleViewButtonClick(record.id)}
              title={t("common.details")}
            />
            <Permission acceptableClaims={[Claims.User.Invite]}>
              <IconButton
                path={mdiEmail}
                onClick={() => handleInviteButtonClick(record.id)}
                title={t("settings.users.invite")}
              />
            </Permission>

            <Permission acceptableClaims={[Claims.User.Delete]}>
              <IconButton
                path={mdiDelete}
                onClick={() => handleDeleteButtonClick(record.id)}
                title={t("common.delete")}
              />
            </Permission>
          </Space>
        );
      },
      title: t("common.table.actions"),
    }),
    [handleViewButtonClick, handleDeleteButtonClick, handleInviteButtonClick, t]
  );

  const customTableProps = useTable({
    fetchTableData,
    columnParams,
    actionColumnParams,
    listParams,
    list: users,
    columnStorageName: ColumnStorageName.USERS,
    headerOptions: (
      <Permission acceptableClaims={[Claims.User.Create]}>
        <Button
          icon={<MaterialIcon path={mdiPlus} />}
          type="primary"
          onClick={() => setCreateModalOpen(true)}
        >
          {t("common.new")}
        </Button>
      </Permission>
    ),
    resetListParams: dispatch.usersListSettings.resetUsersListParams,
    loading: fetchUsers.loading,
    resetStore: dispatch.usersListSettings.reset,
  });

  return (
    <>
      <CustomTable<UserListItemVm>
        {...customTableProps}
        scrollX={1200}
        selectable={false}
      />

      <UserCreateModal
        visible={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          dispatch.usersListSettings.fetchUsers(listParams);
        }}
      />
    </>
  );
};

export default UsersList;
